import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {OBJECT_TYPE_TO_DISPLAY_TEXT, ObjectType} from 'config-migration/types/ObjectType';
import {GRAY_LIGHTER} from 'ui-library/Colors';

import Log, {LogMessageData} from './Log';

export type LogMessageGroup = Array<LogMessageData>;

export interface LogSectionProps {
  logMessageGroups: Array<LogMessageGroup>;
  sectionDisplayName?: string;
  backgroundColor?: string;
}

const DEFAULT_BACKGROUND_COLOR = GRAY_LIGHTER;

const TEMPLATE_SECTION_GROUP: Array<ObjectType> = [
  ObjectType.TemplateCollection,
  ObjectType.Template,
  ObjectType.Subtemplate,
];

const WORKFLOW_SECTION_GROUP: Array<ObjectType> = [
  ObjectType.ProcessTaskSchema,
  ObjectType.ProcessOutputSchema,
  ObjectType.ProcessFlowchartConfigVersion,
];

/**
 * Returns the name of the log section for an object type. Note that the log section name can be different
 * from the import plan section name.
 */
export function getSectionNameForObjectType(objectType: ObjectType): string {
  if (TEMPLATE_SECTION_GROUP.includes(objectType)) {
    return 'Templates';
  }
  if (WORKFLOW_SECTION_GROUP.includes(objectType)) {
    return pluralize(2, OBJECT_TYPE_TO_DISPLAY_TEXT[ObjectType.ProcessTaskSchema]);
  }
  return pluralize(2, OBJECT_TYPE_TO_DISPLAY_TEXT[objectType]);
}

/**
 * Component that renders "groups" of Logs associated with a particular section of the migration. Examples of
 * sections include schema object types like "Result Schema", or a group of object types like "Templates".
 * Each section is rendered with a title, and each group of Logs is separated by a line break.
 */
export default function LogSection(props: LogSectionProps): JSX.Element {
  return (
    <div
      className={classNames(css(styles.logContainer))}
      style={{backgroundColor: props.backgroundColor ? props.backgroundColor : DEFAULT_BACKGROUND_COLOR}}
    >
      {props.sectionDisplayName && (
        <div className={classNames(css(styles.sectionTitle))}>
          <strong>{props.sectionDisplayName}</strong>
        </div>
      )}
      {props.logMessageGroups.map((group, i) => (
        <div
          key={i}
          className={classNames(i !== props.logMessageGroups.length - 1 ? css(styles.logGroup) : null)}
        >
          <Log logMessages={group} />
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  logContainer: {
    padding: 14,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    marginBottom: 10,
  },
  logGroup: {
    marginBottom: 18,
  },
});
